import React, { useState } from 'react';
import { Box, Typography, IconButton, Chip, Collapse, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockIcon from '@mui/icons-material/Lock';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LocalAirportIcon from '@mui/icons-material/LocalAirport';
import MapIcon from '@mui/icons-material/Map';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import { COLORS } from '../styles';
import { getDrvLabel, displayGate } from '../utils';
import MiniConcourseMap from '../MiniConcourseMap';

function AssignmentDetailScreen({ driver, inboundETA, inboundGate, onBack, onReady, mode = 'ack' }) {
  const flights = driver?.flights || [];
  const totalBags = flights.reduce((t, f) => t + (f.bags || 0), 0);
  const hasRunOrder = flights.some(f => f.runOrder);
  const [expandedId, setExpandedId] = useState(null);
  const [mapVisible, setMapVisible] = useState(true);
  // Whole-run dispatcher note (everything except the auto-generated
  // "Run in this order please" — that's already represented by the lock banner).
  const driverNote = (driver?.notes || '')
    .replace(/Run in this order please\.?/gi, '')
    .replace(/^\s*\|\s*|\s*\|\s*$/g, '')
    .replace(/\s*\|\s*/g, ' · ')
    .trim();

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, pb: 1.5, borderBottom: `1px solid ${COLORS.divider}`, display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton size="small" onClick={onBack} sx={{ color: COLORS.textSecondary, p: 0.5 }}>
          <ArrowBackIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '0.6rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Your Run · {getDrvLabel(driver?.name)}
          </Typography>
          <Typography sx={{ fontSize: '0.95rem', color: COLORS.textPrimary, fontWeight: 700, lineHeight: 1.1 }}>
            {flights.length} stops · {totalBags} bags
          </Typography>
        </Box>
      </Box>

      {/* Inbound info */}
      <Box sx={{ px: 2, py: 1.25, backgroundColor: COLORS.surface, borderBottom: `1px solid ${COLORS.divider}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalAirportIcon sx={{ color: COLORS.warn, fontSize: 16, transform: 'rotate(-45deg)' }} />
          <Typography sx={{ fontSize: '0.7rem', color: COLORS.textPrimary, fontWeight: 600 }}>
            Inbound arrives {inboundETA || '?'} at gate {inboundGate || '?'}
          </Typography>
        </Box>
      </Box>

      {/* Concourse map (toggle) */}
      <Box sx={{ mx: 1.5, mt: 1 }}>
        <Box
          onClick={() => setMapVisible(v => !v)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1, py: 0.6,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            borderRadius: mapVisible ? '6px 6px 0 0' : 1.5,
            cursor: 'pointer',
            border: `1px solid ${COLORS.divider}`,
            borderBottom: mapVisible ? 'none' : `1px solid ${COLORS.divider}`,
            '&:active': { backgroundColor: 'rgba(0, 0, 0, 0.55)' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <MapIcon sx={{ color: COLORS.accent, fontSize: 14 }} />
            <Typography sx={{ fontSize: '0.65rem', color: COLORS.textPrimary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6 }}>
              Concourse Map
            </Typography>
          </Box>
          {mapVisible
            ? <KeyboardArrowUpIcon sx={{ color: COLORS.textMuted, fontSize: 16 }} />
            : <KeyboardArrowDownIcon sx={{ color: COLORS.textMuted, fontSize: 16 }} />}
        </Box>
        <Collapse in={mapVisible}>
          <Box sx={{
            borderRadius: '0 0 6px 6px',
            border: `1px solid ${COLORS.divider}`,
            borderTop: 'none',
            overflow: 'hidden',
          }}>
            <MiniConcourseMap driver={driver} inboundGate={inboundGate} />
          </Box>
        </Collapse>
      </Box>

      {/* Stops list (run-order banner + dispatcher note scroll with the tiles) */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {hasRunOrder && (
          <Box sx={{
            p: 1, borderRadius: 1,
            backgroundColor: 'rgba(251, 191, 36, 0.12)',
            border: `1px solid ${COLORS.warn}`,
            display: 'flex', alignItems: 'center', gap: 0.75,
          }}>
            <LockIcon sx={{ fontSize: 14, color: COLORS.warn }} />
            <Typography sx={{ fontSize: '0.7rem', color: COLORS.warn, fontWeight: 700 }}>
              Run in this order
            </Typography>
          </Box>
        )}

        {driverNote && (
          <Box sx={{
            p: 1.25, borderRadius: 1,
            backgroundColor: 'rgba(251, 191, 36, 0.10)',
            border: `1px solid rgba(251, 191, 36, 0.5)`,
            display: 'flex', alignItems: 'flex-start', gap: 0.75,
          }}>
            <StickyNote2Icon sx={{ fontSize: 14, color: COLORS.warn, mt: 0.15 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{
                fontSize: '0.55rem', color: COLORS.warn, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 0.6, lineHeight: 1,
              }}>
                Dispatcher note
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: COLORS.textPrimary, mt: 0.4, lineHeight: 1.35 }}>
                {driverNote}
              </Typography>
            </Box>
          </Box>
        )}

        {flights.map((flight, idx) => {
          const expanded = expandedId === flight.uniqueId;
          const cat = flight.connectionCategory;
          const catColor =
            cat === 'hot' ? COLORS.hot :
            cat === 'cold' ? COLORS.cold :
            cat === 'priority' ? COLORS.warn :
            cat === 'freight-only' ? COLORS.freight :
            COLORS.textSecondary;

          return (
            <Box
              key={flight.uniqueId || idx}
              onClick={() => setExpandedId(expanded ? null : flight.uniqueId)}
              sx={{
                backgroundColor: COLORS.surface,
                borderRadius: 1.5,
                border: `1px solid ${COLORS.divider}`,
                borderLeft: `3px solid ${catColor}`,
                cursor: 'pointer',
                transition: 'background-color 120ms',
                '&:active': { backgroundColor: COLORS.surfaceAlt },
              }}
            >
              {/* Compact row */}
              <Box sx={{ display: 'flex', alignItems: 'center', p: 1.25, gap: 1 }}>
                {/* Stop number */}
                <Box sx={{
                  width: 24, height: 24, borderRadius: '50%',
                  backgroundColor: catColor,
                  color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.7rem', fontWeight: 700,
                  flexShrink: 0,
                }}>
                  {flight.runOrder || idx + 1}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '0.85rem', color: COLORS.textPrimary, fontWeight: 700, lineHeight: 1 }}>
                      {flight.flightNumber}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: COLORS.textSecondary, ml: 0.25 }}>
                      → {flight.destination}
                    </Typography>
                    {(cat === 'hot' || cat === 'cold' || cat === 'priority') && (
                      <Box sx={{
                        fontSize: '0.5rem',
                        fontWeight: 800,
                        color: '#0a1020',
                        backgroundColor: catColor,
                        px: 0.6, py: 0.15,
                        borderRadius: 0.5,
                        letterSpacing: 0.6,
                        ml: 0.5,
                        lineHeight: 1.3,
                      }}>
                        {cat.toUpperCase()}
                      </Box>
                    )}
                    {flight.registration && (
                      <Typography sx={{
                        fontSize: '0.6rem',
                        color: COLORS.accent,
                        fontWeight: 700,
                        ml: 'auto',
                        fontFamily: 'monospace',
                      }}>
                        {flight.registration}
                      </Typography>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: '0.65rem', color: COLORS.textMuted, mt: 0.25 }}>
                    {displayGate(flight)} · ETD {flight.etd} · {flight.bags || 0} bags
                  </Typography>
                  {flight.notes && (
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 0.5,
                      mt: 0.5,
                      px: 0.75, py: 0.5,
                      backgroundColor: 'rgba(251, 191, 36, 0.10)',
                      border: `1px solid rgba(251, 191, 36, 0.4)`,
                      borderRadius: 0.5,
                    }}>
                      <StickyNote2Icon sx={{ fontSize: 11, color: COLORS.warn, mt: 0.15, flexShrink: 0 }} />
                      <Typography sx={{
                        fontSize: '0.65rem',
                        color: COLORS.textPrimary,
                        lineHeight: 1.3,
                        fontStyle: 'italic',
                      }}>
                        {flight.notes}
                      </Typography>
                    </Box>
                  )}
                </Box>
                {expanded ? (
                  <KeyboardArrowUpIcon sx={{ color: COLORS.textMuted, fontSize: 18 }} />
                ) : (
                  <KeyboardArrowDownIcon sx={{ color: COLORS.textMuted, fontSize: 18 }} />
                )}
              </Box>

              {/* Expanded detail */}
              <Collapse in={expanded}>
                <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5, borderTop: `1px solid ${COLORS.divider}` }}>
                  <DetailRow label="Connection" value={formatConnection(flight)} />
                  <BinDistributionGrid binDistribution={flight.binDistribution} />
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>

      {/* Bottom action */}
      <Box sx={{ p: 2, borderTop: `1px solid ${COLORS.divider}` }}>
        <Button
          fullWidth
          onClick={onReady}
          sx={{
            backgroundColor: COLORS.accent,
            color: '#0a1020',
            fontWeight: 700,
            py: 1.25,
            fontSize: '0.85rem',
            '&:hover': { backgroundColor: COLORS.accentDim },
          }}
        >
          {mode === 'summary' ? '◂ BACK TO DRIVE' : "I'M READY ▸"}
        </Button>
      </Box>
    </Box>
  );
}

function DetailRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', mt: 0.5 }}>
      <Typography sx={{ fontSize: '0.65rem', color: COLORS.textMuted, minWidth: 90, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.7rem', color: COLORS.textPrimary, flex: 1 }}>
        {value}
      </Typography>
    </Box>
  );
}

function formatConnection(flight) {
  const m = flight.connectionTime;
  if (m == null) return '—';
  const abs = Math.abs(m);
  const h = Math.floor(abs / 60);
  const mm = abs % 60;
  const sign = m < 0 ? '-' : '';
  return h > 0 ? `${sign}${h}h ${mm}m` : `${sign}${mm}m`;
}

function BinDistributionGrid({ binDistribution }) {
  const dist = binDistribution || {};
  const bins = ['F', 'E', 'D', 'C', 'B', 'A'];
  const total = bins.reduce((sum, b) => sum + (dist[b] || 0), 0);

  return (
    <Box sx={{ mt: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.4 }}>
        <Typography sx={{
          fontSize: '0.6rem',
          color: COLORS.textMuted,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          fontWeight: 700,
        }}>
          Bin Distribution
        </Typography>
        {total > 0 && (
          <Typography sx={{ fontSize: '0.6rem', color: COLORS.textMuted }}>
            {total} total
          </Typography>
        )}
      </Box>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        border: `1px solid ${COLORS.divider}`,
        borderRadius: 0.75,
        overflow: 'hidden',
        backgroundColor: 'rgba(0,0,0,0.25)',
      }}>
        {bins.map((bin, i) => {
          const count = dist[bin] || 0;
          return (
            <Box
              key={bin}
              sx={{
                textAlign: 'center',
                borderLeft: i === 0 ? 'none' : `1px solid ${COLORS.divider}`,
                py: 0.4,
                backgroundColor: count > 0 ? 'rgba(34, 211, 238, 0.08)' : 'transparent',
              }}
            >
              <Typography sx={{
                fontSize: '0.55rem',
                color: COLORS.textMuted,
                fontWeight: 700,
                letterSpacing: 0.5,
                lineHeight: 1,
              }}>
                {bin}
              </Typography>
              <Typography sx={{
                fontSize: '0.85rem',
                color: count > 0 ? COLORS.accent : COLORS.textMuted,
                fontWeight: count > 0 ? 800 : 400,
                lineHeight: 1.2,
                mt: 0.25,
              }}>
                {count > 0 ? count : '—'}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default AssignmentDetailScreen;
